import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import z from 'zod'

import { Contact } from '@/api/hooks/useContacts'
import { useCreateContact } from '@/api/hooks/useCreateContact'
import { useUpdateContact } from '@/api/hooks/useUpdateContact'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogScrollable,
  DialogTitle,
} from '@/components/ui/dialog'
import { Field, FieldError, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useIndividuals } from '@/hooks/useIndividuals'

import { CONTACT_ROLES } from './contacts-table/roles'

// Optional fields are submitted as '' from empty inputs and converted to null on the
// way out, which is what the API treats as "clear this field".
const schema = z.object({
  address: z.string().max(500),
  email: z.union([z.literal(''), z.string().email()]),
  name: z.string().min(1),
  notes: z.string().max(2000),
  organization: z.string().max(200),
  originalRole: z.string().max(200),
  phone: z.string().max(50),
  // Narrowed to the role enum (plus '' for "no role") so the submitted body matches
  // the generated client's role type without a cast.
  role: z.union([z.enum(CONTACT_ROLES), z.literal('')]),
})

type ContactFormValues = z.output<typeof schema>

const emptyValues: ContactFormValues = {
  address: '',
  email: '',
  name: '',
  notes: '',
  organization: '',
  originalRole: '',
  phone: '',
  role: '',
}

const toFormValues = (contact?: Contact | null): ContactFormValues =>
  contact
    ? {
        address: contact.address ?? '',
        email: contact.email ?? '',
        name: contact.name,
        notes: contact.notes ?? '',
        organization: contact.organization ?? '',
        originalRole: contact.originalRole ?? '',
        phone: contact.phone ?? '',
        role: contact.role ?? '',
      }
    : emptyValues

type Props = {
  // Present = edit that contact. Absent = create a new one.
  contact?: Contact | null
  onOpenChange(open: boolean): void
  open: boolean
}

export const ContactDialog = ({ contact, onOpenChange, open }: Props) => {
  const { t } = useTranslation()
  const { selectedIndividualId } = useIndividuals()
  const createContact = useCreateContact()
  const updateContact = useUpdateContact()

  const isEdit = Boolean(contact)

  const form = useForm({
    defaultValues: toFormValues(contact),
    resolver: zodResolver(schema),
  })

  // The dialog is mounted once per row and reused, so the form has to be re-seeded
  // whenever it reopens or the target contact changes.
  useEffect(() => {
    if (open) form.reset(toFormValues(contact))
  }, [contact, form, open])

  const handleSubmit = async (values: ContactFormValues) => {
    if (!selectedIndividualId) return

    // Empty strings are sent as-is: the API treats '' as "clear this field", so a
    // field the user blanked out is actually removed rather than left as it was.
    const requestBody = {
      address: values.address,
      email: values.email,
      name: values.name,
      notes: values.notes,
      organization: values.organization,
      originalRole: values.originalRole,
      phone: values.phone,
      role: values.role,
    }

    if (contact) {
      await updateContact.mutateAsync({
        contactId: contact.id,
        individualId: selectedIndividualId,
        requestBody,
      })
    } else {
      await createContact.mutateAsync({
        individualId: selectedIndividualId,
        requestBody,
      })
    }

    onOpenChange(false)
    form.reset(emptyValues)
  }

  const roleValue = form.watch('role')

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent onOpenAutoFocus={event => event.preventDefault()}>
        <DialogHeader>
          <DialogTitle>
            {isEdit ? t('contactDialog.editTitle') : t('contactDialog.createTitle')}
          </DialogTitle>
          <DialogDescription>
            {isEdit ? t('contactDialog.editDescription') : t('contactDialog.createDescription')}
          </DialogDescription>
        </DialogHeader>
        <DialogScrollable>
          <form className="flex flex-col gap-4" onSubmit={form.handleSubmit(handleSubmit)}>
            <Field>
              <FieldLabel htmlFor="contact-name">{t('contactDialog.fields.name')} *</FieldLabel>
              <Input autoComplete="off" id="contact-name" {...form.register('name')} />
              <FieldError>{form.formState.errors.name?.message}</FieldError>
            </Field>
            <Field>
              <FieldLabel htmlFor="contact-role">{t('contactDialog.fields.role')}</FieldLabel>
              <Select
                onValueChange={value =>
                  // Radix hands back a plain string; the only values it can emit are
                  // the SelectItem values below, which are exactly CONTACT_ROLES.
                  form.setValue('role', value as ContactFormValues['role'], {
                    shouldDirty: true,
                  })
                }
                value={roleValue}
              >
                <SelectTrigger id="contact-role">
                  <SelectValue placeholder={t('contactDialog.fields.rolePlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {CONTACT_ROLES.map(role => (
                    <SelectItem key={role} value={role}>
                      {t(`funkityContactRole_${role}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field>
              <FieldLabel htmlFor="contact-originalRole">
                {t('contactDialog.fields.originalRole')}
              </FieldLabel>
              <Input
                autoComplete="off"
                id="contact-originalRole"
                {...form.register('originalRole')}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="contact-organization">
                {t('contactDialog.fields.organization')}
              </FieldLabel>
              <Input
                autoComplete="off"
                id="contact-organization"
                {...form.register('organization')}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="contact-phone">{t('contactDialog.fields.phone')}</FieldLabel>
              <Input autoComplete="off" id="contact-phone" {...form.register('phone')} />
            </Field>
            <Field>
              <FieldLabel htmlFor="contact-email">{t('contactDialog.fields.email')}</FieldLabel>
              <Input autoComplete="off" id="contact-email" {...form.register('email')} />
              <FieldError>{form.formState.errors.email?.message}</FieldError>
            </Field>
            <Field>
              <FieldLabel htmlFor="contact-address">{t('contactDialog.fields.address')}</FieldLabel>
              <Textarea id="contact-address" rows={2} {...form.register('address')} />
            </Field>
            <Field>
              <FieldLabel htmlFor="contact-notes">{t('contactDialog.fields.notes')}</FieldLabel>
              <Textarea id="contact-notes" rows={3} {...form.register('notes')} />
            </Field>
          </form>
        </DialogScrollable>
        <DialogFooter>
          <div className="flex justify-end gap-2">
            <Button onClick={() => onOpenChange(false)} type="button" variant="outline">
              {t('global.cancel')}
            </Button>
            <Button
              disabled={createContact.isPending || updateContact.isPending}
              onClick={form.handleSubmit(handleSubmit)}
              type="submit"
            >
              {t('global.submit')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
